import {useI18n} from 'i18n/I18nContext'
import {appLocaleToBcp47} from 'i18n/locales'
import {useOrganizationAuth} from 'organization/Auth/Providers/OrganizationAuthProvider'
import {BorderSelector} from 'organization/Event/Certification/Builder/BorderSelector'
import {ColorSelect} from 'organization/Event/Certification/Builder/ColorSelect'
import {FieldEditor} from 'organization/Event/Certification/Builder/FieldEditor'
import {SignaturePad} from 'organization/Event/Certification/Builder/SignaturePad'
import {
  BorderStyle,
  CertificateTemplate,
  TEMPLATES,
} from 'organization/Event/Certification/Builder/templates'
import {TemplateSelector} from 'organization/Event/Certification/Builder/TemplateSelector'
import {CertificateVariables} from 'organization/Event/Certification/Certificate'
import {CertificatePreview} from 'organization/Event/Certification/Preview'
import React, {Dispatch, SetStateAction} from 'react'
export const CertificateBuilder = (props: {
  setTemplate: Dispatch<SetStateAction<CertificateTemplate>>
  template: CertificateTemplate
  hasUnsavedChanges: boolean
}) => {
  const {template, setTemplate, hasUnsavedChanges} = props
  const {user} = useOrganizationAuth()
  const {locale, t} = useI18n()
  const variables: CertificateVariables = {
    Име: user?.first_name,
    Фамилия: user?.last_name,
    Имейл: user?.email,
    Дата: new Date().toLocaleDateString(appLocaleToBcp47(locale)),
    certLocation: t('orgCert.defaultCertLocation'),
  }

  const setTemplateId = (templateId: CertificateTemplate['templateId']) => {
    const template = TEMPLATES.find((t) => t.templateId === templateId)
    if (template) {
      setTemplate((tpl) => {
        return {
          ...template,
          fields: {...tpl.fields},
          layout: {
            ...template.layout,
            themeColor: tpl.layout.themeColor,
            backgroundColor: tpl.layout.backgroundColor,
          },
        }
      })
    }
  }
  const setSignature = (value: string | null) => {
    setTemplate((tpl) => {
      return {
        ...tpl,
        signature: value,
      }
    })
  }
  const setTemplateField = (
    field: keyof CertificateTemplate['fields'],
    value: string,
  ) => {
    setTemplate((tpl) => {
      return {
        ...tpl,
        fields: {...tpl.fields, [field]: value},
      }
    })
  }
  const setTemplateLayout = (
    field: keyof CertificateTemplate['layout'],
    value: string,
  ) => {
    setTemplate((tpl) => {
      return {
        ...tpl,
        layout: {...tpl.layout, [field]: value},
      }
    })
  }
  const setBorderStyle = (value: BorderStyle) => {
    setTemplate((tpl) => {
      return {
        ...tpl,
        decoration: {...tpl.decoration, border: value},
      }
    })
  }
  return (
    <>
      <div className="d-flex justify-content-between">
        <p>
          <b>{t('orgCert.builderPanelTitle')}</b>
        </p>
        <p>
          {hasUnsavedChanges && (
            <b style={{color: 'red'}}>{t('orgCert.builderUnsavedWarning')}</b>
          )}
        </p>
      </div>
      <div className="create__event-main pt-25">
        <div className="event__left-box">
          <div className="create__input-wrapper">
            <TemplateSelector onSelect={setTemplateId} />
            <FieldEditor
              title={t('orgCert.fieldTitle')}
              onChange={(value) => setTemplateField('title', value)}
              defaultValue={template.fields.title}
            />
            <FieldEditor
              title={t('orgCert.fieldSubtitle')}
              onChange={(value) => setTemplateField('subtitle', value)}
              defaultValue={template.fields.subtitle || ''}
            />
            <FieldEditor
              title={t('orgCert.fieldRecipient')}
              onChange={(value) => setTemplateField('recipientName', value)}
              defaultValue={template.fields.recipientName}
            />
            <FieldEditor
              title={t('orgCert.fieldDescription')}
              onChange={(value) => setTemplateField('description', value)}
              defaultValue={template.fields.description || ''}
            />
            <div className="singel__input-field mb-15">
              <label className="input__field-text">
                {t('orgCert.fieldIssuer')}
              </label>
              <input
                type="text"
                name="name"
                placeholder={t('orgCert.fieldIssuerPlaceholder')}
                onChange={(e) => setTemplateField('issuer', e.target.value)}
                defaultValue={template.fields.issuer}
                required
              />
            </div>
            <FieldEditor
              title={t('orgCert.fieldDatePlace')}
              onChange={(value) => setTemplateField('date', value)}
              defaultValue={template.fields.date}
            />
            <BorderSelector
              onChange={(value) => setBorderStyle(value)}
              defaultValue={template.decoration?.border}
            />
            <ColorSelect
              label={t('orgCert.themeBorderColor')}
              onChange={(value) => setTemplateLayout('themeColor', value)}
              defaultValue={template.layout.themeColor}
            />
            <ColorSelect
              label={t('orgCert.themeBackgroundColor')}
              onChange={(value) => setTemplateLayout('backgroundColor', value)}
              defaultValue={template.layout.backgroundColor}
            />
            <div className="singel__input-field mb-15">
              <label className="input__field-text">
                {t('orgCert.previewSignature')}
              </label>
              <SignaturePad
                onSave={setSignature}
                signature={template.signature}
              />
            </div>
          </div>
        </div>
        <div className="event__right-box">
          <CertificatePreview template={template} variables={variables} />
        </div>
      </div>
    </>
  )
}
